<form [formGroup]="form" class="flex flex-col gap-6">
  <p class="text-sm text-content-interactive-subdued flex flex-col gap-3">
    <span class="font-bold">{{
      'ANALYSIS.CREATE_CONFIGURATION.GENERAL' | translate
    }}</span>
    {{ 'ANALYSIS.CREATE_CONFIGURATION.GENERAL_STEP_DESCRIPTION' | translate }}
  </p>

  <!-- name -->
  <pm-form-field
    label="ANALYSIS.NAME"
    [errors]="{ required: 'FORM.VALIDATIONS.REQUIRED_INPUT' }"
  >
    <pm-form-input-text formControlName="name" />
  </pm-form-field>

  <!-- analysis type -->
  <div class="grid grid-cols-2 gap-4">
    @for (option of analysisTypeOptions; track option.value) {
      <pm-selection-card
        [title]="option.titleKey"
        [description]="option.descriptionKey"
        [selected]="analysisType() === option.value"
        (handleSelect)="selectAnalysisType(option.value)"
      />
    }
  </div>

  @if (analysisType() === AnalysisType.ROLLING) {
    <!-- freeze period -->
    <div class="flex flex-col gap-1.5">
      <pm-form-field
        label="ANALYSIS.FREEZE_PERIOD"
        [errors]="{
          required: 'FORM.VALIDATIONS.REQUIRED_INPUT',
          min: 'FORM.VALIDATIONS.MIN_AMOUNT',
        }"
      >
        <pm-form-input-number formControlName="freezePeriod" />
      </pm-form-field>
      <p class="text-xs text-content-interactive-subdued">
        {{ 'ANALYSIS.CREATE_CONFIGURATION.FREEZE_PERIOD_HINT' | translate }}
      </p>
    </div>

    <!-- lookback period -->
    <div class="flex flex-col gap-1.5">
      <pm-form-field
        label="ANALYSIS.LOOKBACK_PERIOD"
        [errors]="{
          required: 'FORM.VALIDATIONS.REQUIRED_INPUT',
          min: 'FORM.VALIDATIONS.MIN_AMOUNT',
        }"
      >
        <pm-form-input-number formControlName="lookbackPeriod" />
      </pm-form-field>
      <p class="text-xs text-content-interactive-subdued">
        {{ 'ANALYSIS.CREATE_CONFIGURATION.LOOKBACK_PERIOD_HINT' | translate }}
      </p>
    </div>

    <!-- time series -->
    <div class="flex flex-col gap-3">
      <span class="text-sm font-bold text-content-interactive-subdued">{{
        'ANALYSIS.CREATE_CONFIGURATION.TIMESERIES_TITLE' | translate
      }}</span>
      <div class="flex flex-row gap-3 flex-wrap">
        <div class="flex flex-row items-center gap-2">
          <pm-icon
            [icon]="LockIcon"
            size="xs"
            class="shrink-0 text-content-subdued"
          />
          <span class="text-xs text-content-default">
            {{
              'ANALYSIS.CREATE_CONFIGURATION.DATA_SOURCE_FORECAST' | translate
            }}</span
          >
        </div>

        <div class="contents" [formGroup]="comparisonTimeseriesNamesForm">
          @for (name of comparisonTimeseriesNames(); track name) {
            @if (comparisonTimeseriesNamesForm.contains(name)) {
              <pm-form-input-checkbox [formControlName]="name">
                <span class="text-xs text-content-default">{{ name }}</span>
              </pm-form-input-checkbox>
            }
          }
        </div>
      </div>
    </div>

    <!-- missing value strategy HIDDEN FOR THE MOMENT-->
    <div class="flex flex-col gap-3" style="display: none">
      <span class="text-sm font-bold text-content-interactive-subdued">{{
        'ANALYSIS.CREATE_CONFIGURATION.SERIES_HAS_NO_VALUE_TITLE' | translate
      }}</span>
      <mat-radio-group
        color="primary"
        formControlName="missingValueStrategy"
        class="flex flex-col gap-0"
      >
        @for (option of missingValueStrategyOptions; track option.value) {
          <mat-radio-button [value]="option.value">
            <span class="text-xs text-content-default">
              {{ option.labelKey | translate }}
            </span>
          </mat-radio-button>
        }
      </mat-radio-group>
    </div>

    <!-- missing value strategy info -->
    <div
      class="flex items-start gap-2 rounded-lg bg-surface-neutral-subdued p-4"
    >
      <pm-icon
        [icon]="InfoIcon"
        size="sm"
        class="mt-0.5 shrink-0 text-content-subdued"
      />
      <p class="text-sm text-content-subdued">
        {{
          'ANALYSIS.CREATE_CONFIGURATION.MISSING_MONTH_STRATEGY_INFO'
            | translate
        }}
      </p>
    </div>
  }
  <!-- what to evaluate -->
  @if (analysisType() === AnalysisType.SINGLE_PERIOD) {
    <span class="text-sm font-bold text-content-interactive-subdued">{{
      'ANALYSIS.CREATE_CONFIGURATION.WHAT_TO_EVALUATE_TITLE' | translate
    }}</span>

    <!-- consensus cycle -->
    <pm-form-field
      label="ANALYSIS.CREATE_CONFIGURATION.WHAT_TO_EVALUATE_CONSENSUS"
      [reserveErrorHeight]="false"
    >
      <pm-form-input-select
        [data]="consensusCycleOptions()"
        [clearable]="true"
        labelKey="label"
        propertyKey="id"
        sortByKey="sortDate"
        sortDirection="desc"
        formControlName="consensusCycleId"
      >
        <span *pmFormInputSelection="let option = node" class="text-sm">
          {{ option.name }}
          <span class="text-content-subdued">- {{ option.suffix }}</span>
        </span>
        <ng-container *pmFormInputSelected>
          @if (selectedConsensusCycle(); as selectedOption) {
            <span class="truncate text-sm">
              {{ selectedOption.name }}
              <span class="text-content-subdued"
                >- {{ selectedOption.suffix }}</span
              >
            </span>
          }
        </ng-container>
      </pm-form-input-select>
    </pm-form-field>

    <!-- forecast version -->
    <pm-form-field
      label="ANALYSIS.CREATE_CONFIGURATION.WHAT_TO_EVALUATE_FORECAST"
      [reserveErrorHeight]="false"
    >
      <pm-form-input-select
        [data]="forecastVersionOptions()"
        [clearable]="true"
        labelKey="label"
        propertyKey="id"
        sortByKey="createdAt"
        sortDirection="desc"
        formControlName="forecastVersionId"
      />
    </pm-form-field>

    @if (evaluationInfoKey(); as infoKey) {
      <div
        class="flex items-start gap-2 rounded-lg bg-surface-neutral-subdued p-4"
      >
        <pm-icon
          [icon]="InfoIcon"
          size="sm"
          class="mt-0.5 shrink-0 text-content-subdued"
        />
        <p class="text-sm text-content-subdued">
          {{ infoKey | translate }}
        </p>
      </div>
    }
  }
</form>
